import { useMemo } from 'react'

import { Experiments } from 'constants/experiments'
import type { SearchTileDensity } from 'components/SearchKit/SearchTiles/searchTileDensity'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { isGHARedirection } from 'lib/optimizely/optimizelyUtils'

interface SearchOfferTileDensityEligibility {
  readonly filters?: App.OfferListFilters
  readonly isMobile: boolean
  readonly pathname: string
  readonly utmBp?: string
  readonly utmContent?: string
  readonly utmMedium?: string
  readonly utmSource?: string
}

export interface SearchOfferTileDensityExperimentValue {
  readonly canActivate: boolean
  readonly density: SearchTileDensity
  readonly isCompact: boolean
}

const GENERAL_SEARCH_SEGMENT = 'search'
const METASEARCH_PATH_SEGMENTS = ['metasearch'] as const
const EXCLUDED_GENERAL_SEARCH_PATH_SEGMENTS = [
  METASEARCH_PATH_SEGMENTS,
  ['search-fullscreen'],
  ['search', 'homes-and-villas'],
  ['search', 'experiences'],
  ['search', 'cruises'],
  ['search', 'tours'],
  ['search', 'car-hire'],
  ['search', 'shop'],
] as const
const INCLUDED_GENERAL_SEARCH_PATH_SEGMENTS = [
  ['search', 'map-split'],
  ['search', 'store', 'map'],
  ['search', 'map'],
] as const

function getPathSegments(pathname: string): Array<string> {
  return pathname.split('/').filter(Boolean)
}

function hasPathSegmentSequence(pathSegments: ReadonlyArray<string>, expectedSegments: ReadonlyArray<string>): boolean {
  return pathSegments.some((_, startIndex) =>
    expectedSegments.every((expectedSegment, segmentIndex) => pathSegments[startIndex + segmentIndex] === expectedSegment),
  )
}

export function isGeneralAccommodationSearchResultsPath(pathname: string): boolean {
  const pathSegments = getPathSegments(pathname)
  const isExcludedPath = EXCLUDED_GENERAL_SEARCH_PATH_SEGMENTS.some(excludedPathSegments =>
    hasPathSegmentSequence(pathSegments, excludedPathSegments),
  )
  if (isExcludedPath) return false

  const isIncludedPath = INCLUDED_GENERAL_SEARCH_PATH_SEGMENTS.some(includedPathSegments =>
    hasPathSegmentSequence(pathSegments, includedPathSegments),
  )
  if (isIncludedPath) return true

  return pathSegments[pathSegments.length - 1] === GENERAL_SEARCH_SEGMENT
}

export function isPropertyMetasearchResultsPath(pathname: string): boolean {
  return hasPathSegmentSequence(getPathSegments(pathname), METASEARCH_PATH_SEGMENTS)
}

export function canActivateSearchOfferTileDensityExperiment({
  filters,
  isMobile,
  pathname,
  utmBp,
  utmContent,
  utmMedium,
  utmSource,
}: SearchOfferTileDensityEligibility): boolean {
  const isGoogleSearchTraffic = utmSource === 'google' && (utmMedium === 'cpc' || utmMedium === 'organic')
  const isGoogleHotelAdsTraffic = utmBp === '3' && isGHARedirection(utmContent)
  const isEligibleSearchTraffic = isGoogleSearchTraffic || isGoogleHotelAdsTraffic

  const isGeneralSearch = isEligibleSearchTraffic &&
    isGeneralAccommodationSearchResultsPath(pathname)

  const isPropertyMetasearch = isEligibleSearchTraffic &&
    isPropertyMetasearchResultsPath(pathname) &&
    !!filters?.propertyId

  return isMobile &&
    (isGeneralSearch || isPropertyMetasearch)
}

interface SearchOfferTileDensityExperimentOptions {
  /**
   * When false, the Optimizely compact experiment cannot activate.
   * Used so CRO-6924 mosaic (HotelSearchPage) and Meta Search Mobile V2
   * do not co-activate denser tiles.
   * @default true
   */
  enabled?: boolean
}

function useSearchOfferTileDensityExperiment(
  filters?: App.OfferListFilters,
  options: SearchOfferTileDensityExperimentOptions = {},
): SearchOfferTileDensityExperimentValue {
  const { enabled = true } = options
  const pathname = useAppSelector(state => state.router.location.pathname)
  const utmBp = useAppSelector(state => state.utm.bp)
  const utmContent = useAppSelector(state => state.utm.content)
  const utmSource = useAppSelector(state => state.utm.source)
  const utmMedium = useAppSelector(state => state.utm.medium)
  // Metasearch: UA (SSR-stable). General search: viewport (unchanged).
  const isMobileViewport = useScreenSizeOnly('mobile')
  const isMobileDevice = useIsMobileDevice()
  const isMobile = isPropertyMetasearchResultsPath(pathname) ? isMobileDevice : isMobileViewport

  const canActivate = useMemo(
    () => enabled && canActivateSearchOfferTileDensityExperiment({
      filters,
      isMobile,
      pathname,
      utmBp,
      utmContent,
      utmMedium,
      utmSource,
    }),
    [enabled, filters, isMobile, pathname, utmBp, utmContent, utmMedium, utmSource],
  )

  const { isAnyVariant } = useExperiment(
    Experiments.commsCompactMobileSearchTile,
    { canActivate },
  )

  const density: SearchTileDensity = isAnyVariant && canActivate ? 'compact' : 'default'

  return useMemo(
    () => ({
      canActivate,
      density,
      isCompact: density === 'compact',
    }),
    [canActivate, density],
  )
}

export default useSearchOfferTileDensityExperiment
